import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './Dashboard.css';

// NOTE: Remplacement de la référence statique au logo par une logique
// qui teste plusieurs emplacements (PUBLIC_URL, API/static via buildUrl, racine).
// Ceci permet d'éviter les problèmes lorsque le fichier est servi depuis un autre host/port.
// Le fallback est un petit SVG embarqué (data URL).

const globalStyle = `
    html, body {
        background: #0a0e1a !important;
        min-height: 100vh;
        margin: 0;
        padding: 0;
    }
    * {
        box-sizing: border-box;
    }
    @keyframes slideInRight {
        from {
            transform: translateX(400px);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    @keyframes slideOutRight {
        from {
            transform: translateX(0);
            opacity: 1;
        }
        to {
            transform: translateX(400px);
            opacity: 0;
        }
    }
    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
    .user-location-marker {
        background: transparent !important;
        border: none !important;
    }
`;

const entrepriseIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28]
});

const selectedIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
    iconSize: [38, 38],
    iconAnchor: [19, 38],
    popupAnchor: [0, -38]
});

// Icône pour la position utilisateur - version simple et robuste
const userLocationIcon = new L.DivIcon({
    html: `
        <div style="
            width: 24px; 
            height: 24px; 
            background: #3B82F6; 
            border: 4px solid #FFFFFF; 
            border-radius: 50%; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            position: relative;
        ">
            <div style="
                width: 8px; 
                height: 8px; 
                background: #FFFFFF; 
                border-radius: 50%; 
                position: absolute; 
                top: 50%; 
                left: 50%; 
                transform: translate(-50%, -50%);
            "></div>
        </div>
    `,
    className: 'user-location-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
});

function FlyToMarker({ position }) {
    const map = useMap();
    useEffect(() => {
        if (position && map && map.getContainer()) {
            try {
                map.flyTo(position, 15, { duration: 1 });
            } catch (error) {
                console.warn('Erreur flyTo:', error);
                // Fallback: utiliser setView sans animation
                setTimeout(() => {
                    try {
                        map.setView(position, 15);
                    } catch (fallbackError) {
                        console.warn('Erreur setView:', fallbackError);
                    }
                }, 100);
            }
        }
    }, [position, map]);
    return null;
}

export default function Dashboard() {
    const [history, setHistory] = useState([]);
    const [selectedActivity, setSelectedActivity] = useState('');
    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const [rayon, setRayon] = useState(10);
    const [user, setUser] = useState({});
    const [codePostal, setCodePostal] = useState(''); // Code département (2 chiffres)
    const [codePostalComplet, setCodePostalComplet] = useState(''); // Code postal complet (5 chiffres)
    const [nbResult, setNbResult] = useState(100);
    const [results, setResults] = useState([]);
    const [selectedEtab, setSelectedEtab] = useState(null);
    const [showList, setShowList] = useState(true);
    const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
    const [fullscreen, setFullscreen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false); // État de chargement pour les recherches
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('search');
    const [mapReady, setMapReady] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth <= 768);
    const [isMobile, setIsMobile] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Logo dynamique : on teste plusieurs emplacements et on choisit le premier accessible.
    const [logoSrc, setLogoSrc] = useState(() => {
        // valeur initiale raisonnable : PUBLIC_URL si défini, sinon racine
        return (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/logo.jpg` : '/logo.jpg');
    });

    useEffect(() => {
        let mounted = true;
        async function probe() {
            const candidates = [
                process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/logo.jpg` : null,
                buildUrl('/logo.jpg'),
                '/logo.jpg',
                `${window.location.origin}/logo.jpg`
            ].filter(Boolean);

            for (const url of candidates) {
                try {
                    // HEAD permet de vérifier l'existence sans télécharger le body
                    const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
                    if (res.ok) {
                        if (mounted) {
                            setLogoSrc(url);
                        }
                        return;
                    }
                } catch (e) {
                    // ignore et tester l'URL suivante
                }
            }
            // Aucun logo accessible -> on force le fallback en null
            if (mounted) setLogoSrc(null);
        }
        probe();
        return () => { mounted = false; };
    }, []); // run once

    function buildUrl(path) {
        const base = (process.env.REACT_APP_API_URL || "http://localhost:5050").trim();
        if (!base) return path.startsWith("/") ? path : `/${path}`;
        return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
    }

    function getToken() {
        try {
            return localStorage.getItem('token') || localStorage.getItem('authToken');
        } catch (e) {
            return null;
        }
    }

    // Fonction pour vérifier et demander les permissions de géolocalisation
    const checkGeolocationPermission = async () => {
        if (!navigator.permissions) {
            return 'unsupported';
        }

        try {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            return permission.state; // 'granted', 'denied', 'prompt'
        } catch (error) {
            console.warn('Erreur lors de la vérification des permissions:', error);
            return 'unknown';
        }
    };

    // Détection iOS spécifique
    const isIOS = () => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    };

    const isAndroid = () => {
        return /Android/.test(navigator.userAgent);
    };

    // Fonction spéciale pour iOS
    const getCurrentLocationIOS = () => {
        return new Promise((resolve, reject) => {
            // Options spécifiques iOS
            const options = {
                enableHighAccuracy: true,
                timeout: 30000, // iOS a besoin de plus de temps
                maximumAge: 300000 // 5 minutes
            };

            // Safari iOS nécessite une approche différente
            const timeoutId = setTimeout(() => {
                reject(new Error('TIMEOUT_IOS'));
            }, 30000);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    clearTimeout(timeoutId);
                    resolve(position);
                },
                (error) => {
                    clearTimeout(timeoutId);
                    reject(error);
                },
                options
            );
        });
    };

    // Fonction pour obtenir la géolocalisation de l'utilisateur
    const getCurrentLocation = async () => {
        if (!navigator.geolocation) {
            setLocationError("La géolocalisation n'est pas supportée par ce navigateur");
            showNotification('❌ Géolocalisation non supportée', 'Votre navigateur ne supporte pas la géolocalisation', 'error');
            return;
        }

        const isIOSDevice = isIOS();
        const isAndroidDevice = isAndroid();

        console.log('  Détection appareil:', { isIOSDevice, isAndroidDevice, userAgent: navigator.userAgent });

        // Vérification HTTPS pour iOS (obligatoire)
        if (isIOSDevice && location.protocol !== 'https:' && location.hostname !== 'localhost') {
            showNotification(
                '🍎 iOS - HTTPS requis',
                'Sur iPhone/iPad, la géolocalisation nécessite obligatoirement une connexion HTTPS sécurisée.',
                'error'
            );
            return;
        }

        // Message spécifique iOS
        if (isIOSDevice) {
            showNotification(
                '🍎 Localisation iOS',
                'Touchez "Autoriser" dans la popup Safari qui va apparaître, puis patientez...',
                'info'
            );
        }

        setLocating(true);
        setLocationError(null);

        try {
            let position;

            if (isIOSDevice) {
                // Méthode spéciale pour iOS
                position = await getCurrentLocationIOS();
            } else {
                // Méthode standard pour autres appareils
                const options = {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 600000
                };

                if (isAndroidDevice) {
                    options.enableHighAccuracy = false; // Android peut être plus lent avec high accuracy
                    options.timeout = 20000;
                }

                position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, options);
                });
            }

            // Traitement de la position obtenue
            const { latitude, longitude } = position.coords;
            const newLocation = [latitude, longitude];

            console.log('🎯 Position obtenue:', { latitude, longitude, accuracy: position.coords.accuracy });
            setUserLocation(newLocation);
            setMapCenter(newLocation);
            setLocating(false);

            // Géocodage inverse
            reverseGeocode(latitude, longitude);

            showNotification(
                '✅ Position trouvée!',
                `Précision: ${Math.round(position.coords.accuracy)}m`,
                'success'
            );

        } catch (error) {
            setLocating(false);
            console.error('❌ Erreur géolocalisation:', error);

            let errorTitle = '❌ Localisation échouée';
            let errorMessage = '';

            if (error.message === 'TIMEOUT_IOS') {
                errorTitle = '🍎 Timeout iOS';
                errorMessage = 'La localisation prend trop de temps sur iOS. Vérifiez que:\n• Réglages > Confidentialité > Services de localisation > Safari > Pendant l\'utilisation\n• Vous êtes à l\'extérieur ou près d\'une fenêtre\n• La connexion réseau est stable';
            } else {
                switch (error.code) {
                    case 1: // PERMISSION_DENIED
                        if (isIOSDevice) {
                            errorTitle = '🍎 Accès refusé sur iOS';
                            errorMessage = 'Pour autoriser la géolocalisation sur iPhone:\n1. Ouvrez Réglages > Safari > Localisation\n2. Choisissez "Pendant l\'utilisation de l\'app"\n3. Rechargez cette page\n4. Touchez "Autoriser" quand Safari le demande';
                        } else {
                            errorMessage = 'Géolocalisation refusée. Autorisez l\'accès dans les paramètres de votre navigateur.';
                        }
                        break;
                    case 2: // POSITION_UNAVAILABLE  
                        errorMessage = isIOSDevice ?
                            'Position non disponible. Vérifiez que vous êtes à l\'extérieur et que les services de localisation sont activés dans Réglages > Confidentialité.' :
                            'Position non disponible. Vérifiez votre connexion GPS.';
                        break;
                    case 3: // TIMEOUT
                        errorMessage = isIOSDevice ?
                            'Délai dépassé. Sur iOS, la géolocalisation peut prendre plus de temps. Réessayez à l\'extérieur.' :
                            'Délai dépassé. Réessayez dans un endroit avec une meilleure réception.';
                        break;
                    default:
                        errorMessage = 'Erreur inconnue. Vérifiez vos paramètres de géolocalisation.';
                }
            }

            setLocationError(errorMessage);
            showNotification(errorTitle, errorMessage, 'error');
        }
    };

    // Fonction de géocodage inverse pour récupérer le code postal
    const reverseGeocode = async (lat, lng) => {
        try {
            // Essayer d'abord avec une API via un proxy ou JSONP pour éviter CORS
            // Méthode alternative 1: Utiliser l'API du gouvernement français
            const govApiUrl = `https://api-adresse.data.gouv.fr/reverse/?lon=${lng}&lat=${lat}`;

            try {
                const govResponse = await fetch(govApiUrl);
                if (govResponse.ok) {
                    const govData = await govResponse.json();
                    if (govData.features && govData.features.length > 0) {
                        const feature = govData.features[0];
                        const postcode = feature.properties.postcode;
                        if (postcode) {
                            const departmentCode = postcode.toString().substring(0, 2);
                            setCodePostal(departmentCode);
                            console.log('Code postal détecté via API Gouv:', departmentCode);
                            showNotification('📍 Localisation réussie', `Département détecté: ${departmentCode}`, 'success');
                            return;
                        }
                    }
                }
            } catch (govError) {
                console.warn('Erreur API Gouvernement:', govError);
            }

            // Méthode alternative 2: Estimation basée sur les coordonnées (approximative)
            const estimatedDepartment = estimateDepartmentFromCoords(lat, lng);
            if (estimatedDepartment) {
                setCodePostal(estimatedDepartment);
                console.log('Département estimé:', estimatedDepartment);
                showNotification('📍 Localisation approximative', `Département estimé: ${estimatedDepartment}`, 'info');
                return;
            }

            // Si tout échoue, on affiche juste la position sans le département
            showNotification('📍 Position détectée', 'Département non déterminé automatiquement', 'info');

        } catch (error) {
            console.warn('Erreur lors du géocodage inverse:', error);
            showNotification('📍 Position détectée', 'Département non déterminé automatiquement', 'info');
        }
    };

    // Fonction d'estimation approximative du département basée sur les coordonnées
    const estimateDepartmentFromCoords = (lat, lng) => {
        // Grille approximative des départements français
        // Cette méthode est approximative mais évite les appels API
        const departmentBounds = [
            // Format: [latMin, latMax, lngMin, lngMax, département]

            // Sud de la France (région méditerranéenne)
            [43.2, 43.9, 3.0, 4.8, '30'], // Gard
            [44.0, 45.0, 3.8, 4.8, '07'], // Ardèche
            [43.4, 44.2, 4.2, 5.8, '84'], // Vaucluse
            [43.1, 43.8, 5.3, 7.0, '83'], // Var
            [43.4, 44.4, 6.0, 7.7, '06'], // Alpes-Maritimes
            [42.3, 43.0, 1.7, 3.2, '66'], // Pyrénées-Orientales
            [42.9, 43.9, 0.1, 2.2, '11'], // Aude
            [43.0, 44.0, 2.8, 4.2, '34'], // Hérault
            [43.2, 44.5, 4.8, 6.8, '13'], // Bouches-du-Rhône

            // Centre et autres régions importantes
            [44.5, 45.5, 4.2, 5.2, '42'], // Loire
            [45.5, 46.5, 4.5, 5.5, '69'], // Rhône (Lyon)
            [48.5, 49.0, 2.0, 2.7, '75'], // Paris
            [48.7, 49.2, 2.0, 2.5, '92'], // Hauts-de-Seine
            [48.6, 49.1, 2.2, 2.9, '93'], // Seine-Saint-Denis
            [48.5, 48.9, 2.2, 2.9, '94'], // Val-de-Marne

            // Nord
            [50.0, 51.1, 1.5, 4.2, '59'], // Nord
            [49.2, 50.5, 1.4, 4.2, '62'], // Pas-de-Calais

            // Ouest
            [47.0, 48.7, -5.2, -0.5, '35'], // Ille-et-Vilaine
            [46.2, 48.0, -4.8, -0.8, '44'], // Loire-Atlantique
            [46.0, 47.8, -2.5, 0.2, '49'], // Maine-et-Loire

            // Est
            [47.4, 49.5, 6.8, 8.3, '68'], // Haut-Rhin
            [48.0, 49.2, 6.0, 7.7, '67'], // Bas-Rhin
            [47.8, 49.6, 5.4, 7.0, '88'], // Vosges

            // Centre
            [47.2, 48.5, 1.0, 3.2, '45'], // Loiret
            [46.5, 47.8, 0.0, 2.8, '37'], // Indre-et-Loire
        ];

        for (const [latMin, latMax, lngMin, lngMax, dept] of departmentBounds) {
            if (lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax) {
                return dept;
            }
        }

        // Si aucune correspondance exacte, essayer une estimation basique
        // France métropolitaine approximative: 42-51 lat, -5-8 lng
        if (lat >= 42 && lat <= 51 && lng >= -5 && lng <= 8) {
            // Estimation très basique basée sur la position relative
            if (lat < 44) return '66'; // Sud (Pyrénées-Orientales par défaut)
            if (lat > 48) return '59'; // Nord (Nord par défaut)
            if (lng < 2) return '35'; // Ouest (Ille-et-Vilaine par défaut)
            if (lng > 6) return '68'; // Est (Haut-Rhin par défaut)
            return '45'; // Centre (Loiret par défaut)
        }

        return null; // Hors France métropolitaine
    };

    // Fonction pour afficher les notifications
    const showNotification = (title, message, type = 'info') => {
        const notification = document.createElement('div');
        const bgColor = type === 'success' ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.95))' :
            type === 'error' ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95))' :
                'linear-gradient(135deg, rgba(59, 130, 246, 0.95), rgba(37, 99, 235, 0.95))';

        notification.style.cssText = `
            position: fixed;
            top: 80px;
            right: 24px;
            background: ${bgColor};
            backdrop-filter: blur(12px);
            color: #fff;
            padding: 16px 20px;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
            border: 1px solid rgba(255, 255, 255, 0.2);
            z-index: 10000;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 12px;
            animation: slideInRight 0.3s ease-out;
            min-width: 300px;
            max-width: 400px;
        `;

        notification.innerHTML = `
            <span style="font-size: 20px;">${type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}</span>
            <div>
                <div style="font-weight: 700; margin-bottom: 4px;">${title}</div>
                <div style="font-size: 12px; opacity: 0.9;">${message}</div>
            </div>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            notification.style.animation = 'slideOutRight 0.3s ease-out';
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.remove();
                }
            }, 300);
        }, 4000);
    };

    useEffect(() => {
        let mounted = true;
        async function load() {
            setLoading(true);
            try {
                const token = getToken();
                if (!token) {
                    navigate('/login', { replace: true });
                    return;
                }

                const usernameQuery = new URLSearchParams(location.search).get('username');
                let headers = {};
                let url = buildUrl('/api/dashboard');
                if (token) headers['Authorization'] = 'Bearer ' + token;
                if (!token && usernameQuery) url += '?username=' + encodeURIComponent(usernameQuery);

                const res = await fetch(url, { headers });
                const resData = await res.json().catch(() => ({}));

                if (!mounted) return;

                if (!res.ok) {
                    if (res.status === 401 || res.status === 403 || res.status === 404) {
                        localStorage.clear();
                        navigate('/login', { replace: true });
                        return;
                    }
                    navigate('/login', { replace: true });
                    return;
                }

                setData(resData);
                if (resData.user) {
                    localStorage.setItem('user', JSON.stringify(resData.user));
                }
            } catch (e) {
                if (mounted) navigate('/login', { replace: true });
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, [location.search, navigate]);

    useEffect(() => {
        if (data?.user) {
            setUser(data.user);
            localStorage.setItem('prospectys_user', JSON.stringify(data.user));
            if (!data.user.two_factor_enabled) {
                localStorage.clear();
                navigate('/login');
                return;
            }
        }
    }, [data, navigate]);

    useEffect(() => {
        if (!user.username) return;

        console.log('[HISTORIQUE] Chargement pour:', user.username);

        // Charger d'abord depuis localStorage
        const savedHistory = localStorage.getItem('prospectys_history');
        if (savedHistory) {
            try {
                const parsed = JSON.parse(savedHistory);
                console.log('[HISTORIQUE] Chargé depuis localStorage:', parsed.length, 'entrées');
                setHistory(parsed);
            } catch (e) {
                console.error('[HISTORIQUE] Erreur parse localStorage:', e);
            }
        }

        // Puis charger l'historique depuis le backend
        fetch(buildUrl(`/api/search-history?username=${user.username}`))
            .then(res => res.json())
            .then(data => {
                console.log('[HISTORIQUE] Reçu du backend:', data);
                if (Array.isArray(data) && data.length > 0) {
                    // Mapper les données backend vers le format frontend
                    const mappedHistory = data.map(entry => ({
                        date: new Date(entry.search_time || entry.search_date).toLocaleString('fr-FR'),
                        code_naf: entry.naf_code || null,
                        code_postal: entry.department || '',
                        rayon: entry.rayon || null,
                        limit: entry.nombre_resultats || 100,
                        // Garder les données originales pour debug
                        _original: entry
                    }));

                    setHistory(mappedHistory);
                    localStorage.setItem('prospectys_history', JSON.stringify(mappedHistory));
                    console.log('[HISTORIQUE] Mis à jour avec', mappedHistory.length, 'entrées mappées');
                }
            })
            .catch((err) => {
                console.error('[HISTORIQUE] Erreur backend:', err);
            });
    }, [user.username]);

    useEffect(() => {
        fetch('/naf_codes.json')
            .then(res => res.json())
            .then(data => { setActivities(data); setLoadingActivities(false); })
            .catch(() => { setActivities([]); setLoadingActivities(false); });
    }, []);

    // Détecter si on est sur mobile
    useEffect(() => {
        const checkIsMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            // Sur mobile, fermer la sidebar par défaut au changement de taille
            if (mobile && !sidebarCollapsed) {
                setSidebarCollapsed(true);
            }
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();

        // Activer le chargement
        setSearchLoading(true);
        setResults([]); // Vider les résultats précédents

        const body = {
            code_naf: selectedActivity || null,
            code_postal: codePostalComplet || codePostal || '', // Priorité au code postal complet, puis département
            rayon: rayon || null,
            limit: nbResult || 100
        };

        try {
            const url = buildUrl('/api/search-etablissements');
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            let validResults = (data.results || []).filter(etab => {
                if (selectedActivity && etab.code_naf !== selectedActivity) return false;
                return true;
            });

            // Enrichir les entreprises sans nom avec l'API INSEE
            console.log('[RECHERCHE] Enrichissement des noms d\'entreprises...');
            const enrichedResults = await Promise.all(
                validResults.map(async (etab) => {
                    // Si pas de nom d'entreprise ou nom générique, essayer de récupérer le vrai nom
                    if (!etab.denomination_usuelle ||
                        etab.denomination_usuelle.startsWith('Entreprise ') ||
                        etab.denomination_usuelle === etab.siren) {

                        try {
                            const nameResponse = await fetch(buildUrl('/api/get-nom-entreprise'), {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ siren: etab.siren })
                            });

                            if (nameResponse.ok) {
                                const nameData = await nameResponse.json();
                                if (nameData.success && nameData.nom) {
                                    console.log(`[ENRICHISSEMENT] ${etab.siren}: ${nameData.nom}`);
                                    return {
                                        ...etab,
                                        denomination_usuelle: nameData.nom,
                                        nom_entreprise: nameData.nom
                                    };
                                }
                            }
                        } catch (nameError) {
                            console.warn(`[ENRICHISSEMENT] Erreur pour ${etab.siren}:`, nameError.message);
                        }
                    }
                    return etab;
                })
            );

            setResults(enrichedResults);

            console.log('[RECHERCHE] Sauvegarde dans historique');

            // Sauvegarder dans l'historique backend IMMÉDIATEMENT
            if (user.username) {
                try {
                    const saveResponse = await fetch(buildUrl('/api/save-search-history'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            username: user.username,
                            code_naf: selectedActivity || null,
                            code_postal: codePostal || '',
                            rayon: rayon || null,
                            limit: nbResult || 100
                        })
                    });

                    const saveResult = await saveResponse.json();
                    console.log('[RECHERCHE] Sauvegarde backend:', saveResult);

                    // RECHARGER l'historique depuis le backend après sauvegarde
                    if (saveResult.success) {
                        try {
                            const historyResponse = await fetch(buildUrl(`/api/search-history?username=${user.username}`));
                            const historyData = await historyResponse.json();

                            if (Array.isArray(historyData) && historyData.length > 0) {
                                const mappedHistory = historyData.map(entry => ({
                                    date: new Date(entry.search_time || entry.search_date).toLocaleString('fr-FR'),
                                    code_naf: entry.naf_code || null,
                                    code_postal: entry.department || '',
                                    rayon: entry.rayon || null,
                                    limit: entry.nombre_resultats || 100,
                                    _original: entry
                                }));

                                setHistory(mappedHistory);
                                localStorage.setItem('prospectys_history', JSON.stringify(mappedHistory));
                                console.log('[RECHERCHE] Historique rechargé:', mappedHistory.length, 'entrées');
                            }
                        } catch (historyErr) {
                            console.error('[RECHERCHE] Erreur rechargement historique:', historyErr);
                        }
                    }
                } catch (err) {
                    console.error('[RECHERCHE] Erreur sauvegarde historique:', err);

                    // Fallback: sauvegarder en local uniquement
                    const newEntry = {
                        date: new Date().toLocaleString('fr-FR'),
                        code_naf: selectedActivity || null,
                        code_postal: codePostal || '',
                        rayon: rayon || null,
                        limit: nbResult || 100
                    };

                    const newHistory = [newEntry, ...history].slice(0, 10);
                    setHistory(newHistory);
                    localStorage.setItem('prospectys_history', JSON.stringify(newHistory));
                    console.log('[RECHERCHE] Sauvegarde locale fallback');
                }
            }
        } catch (error) {
            setResults([]);
            // Notification d'erreur stylée
            const errorNotification = document.createElement('div');
            errorNotification.style.cssText = `
                position: fixed;
                top: 80px;
                right: 24px;
                background: linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95));
                backdrop-filter: blur(12px);
                color: #fff;
                padding: 16px 20px;
                border-radius: 12px;
                box-shadow: 0 8px 32px rgba(239, 68, 68, 0.4);
                border: 1px solid rgba(255, 255, 255, 0.2);
                z-index: 10000;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                display: flex;
                align-items: center;
                gap: 12px;
                animation: slideInRight 0.3s ease-out;
                min-width: 300px;
            `;
            errorNotification.innerHTML = `
                <span style="font-size: 20px;">⚠️</span>
                <div>
                    <div style="font-weight: 700; margin-bottom: 4px;">Erreur de recherche</div>
                    <div style="font-size: 12px; opacity: 0.9;">${error.message}</div>
                </div>
            `;
            document.body.appendChild(errorNotification);
            setTimeout(() => {
                errorNotification.style.animation = 'slideOutRight 0.3s ease-out';
                setTimeout(() => errorNotification.remove(), 300);
            }, 4000);
        } finally {
            // Désactiver le chargement dans tous les cas
            setSearchLoading(false);
        }
    };

    const handleRelancer = (entry) => {
        setSelectedActivity(entry.code_naf || '');

        // Déterminer si c'est un département (2 chiffres) ou un code postal complet (5 chiffres)
        const codePostalValue = entry.code_postal || '';
        if (codePostalValue.length === 5 && /^\d{5}$/.test(codePostalValue)) {
            // Code postal complet (5 chiffres)
            setCodePostalComplet(codePostalValue);
            setCodePostal(codePostalValue.substring(0, 2)); // Extraire le département
        } else if (codePostalValue.length === 2 && /^\d{2}$/.test(codePostalValue)) {
            // Code département (2 chiffres)
            setCodePostal(codePostalValue);
            setCodePostalComplet('');
        } else {
            // Valeur inconnue, mettre dans département par défaut
            setCodePostal(codePostalValue);
            setCodePostalComplet('');
        }

        setRayon(entry.rayon || 10);
        setNbResult(entry.limit || 100);
    };

    const handleSupprimer = async (idx) => {
        const entryToDelete = history[idx];
        const newHistory = history.filter((_, i) => i !== idx);
        setHistory(newHistory);
        localStorage.setItem('prospectys_history', JSON.stringify(newHistory));

        // Supprimer aussi du backend si possible
        if (user.username && entryToDelete) {
            try {
                await fetch(buildUrl('/api/delete-search-history'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        username: user.username,
                        date: entryToDelete.date
                    })
                });
            } catch (err) {
                console.error('Erreur suppression historique backend:', err);
            }
        }
    };

    const isResultsArray = Array.isArray(results);

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0e1a', color: '#fff', fontSize: 18, flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 32 }}>⚙️</div>
            <div>Chargement...</div>
        </div>
    );

    if (!data) {
        navigate('/login', { replace: true });
        return null;
    }

    return (
        <>
            <style>{globalStyle}</style>
            <div className="dashboard-container">
                {/* Header fixe */}
                <nav className="dashboard-nav">
                    <div className="nav-brand">
                        <img
                            src={logoSrc || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='36' height='36'><rect width='36' height='36' rx='8' fill='%23111' /><text x='50%' y='55%' font-size='16' font-family='Arial' fill='%23fff' text-anchor='middle' alignment-baseline='middle'>P</text></svg>"}
                            alt="Logo"
                            className="nav-logo"
                            onError={(e) => {
                                // si l'image 404 ou bloque, afficher le fallback SVG embarqué
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='36' height='36'><rect width='36' height='36' rx='8' fill='%23111' /><text x='50%' y='55%' font-size='16' font-family='Arial' fill='%23fff' text-anchor='middle' alignment-baseline='middle'>P</text></svg>";
                            }}
                        />
                        <span className="nav-title">Prospectys</span>
                    </div>

                    <div className="nav-actions">
                        <div className="user-badge">
                            <div className="user-avatar">
                                {user.username?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <span className="user-name">{user.username}</span>
                            {user.role === 'admin' && <span className="user-role">ADMIN</span>}
                        </div>

                        {user.role === 'admin' && (
                            <button onClick={() => navigate('/configuration')} className="btn-config">
                                ⚙️ Config
                            </button>
                        )}

                        <button onClick={() => { localStorage.clear(); navigate('/login'); }} className="btn-logout">
                            Déconnexion
                        </button>
                    </div>
                </nav>

                {/* Container principal avec sidebar + contenu */}
                <main className="dashboard-main">
                    {/* Bouton toggle pour mobile */}
                    {isMobile && (
                        <button
                            className="sidebar-toggle"
                            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        >
                            {sidebarCollapsed ? 'Afficher les résultats' : 'Masquer les résultats'}
                        </button>
                    )}

                    <div className="results-container">
                        {/* Sidebar gauche - Recherche et résultats */}
                        <aside className={`results-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                            {/* Onglets */}
                            <div style={{ display: 'flex', borderBottom: '1px solid rgba(99, 102, 241, 0.1)' }}>
                                <button
                                    onClick={() => setActiveTab('search')}
                                    style={{
                                        flex: 1,
                                        padding: '16px',
                                        background: activeTab === 'search' ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                                        color: activeTab === 'search' ? '#a78bfa' : '#6b7280',
                                        border: 'none',
                                        borderBottom: activeTab === 'search' ? '2px solid #6366f1' : '2px solid transparent',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    🔍 Recherche
                                </button>
                                <button
                                    onClick={() => setActiveTab('history')}
                                    style={{
                                        flex: 1,
                                        padding: '16px',
                                        background: activeTab === 'history' ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                                        color: activeTab === 'history' ? '#a78bfa' : '#6b7280',
                                        border: 'none',
                                        borderBottom: activeTab === 'history' ? '2px solid #6366f1' : '2px solid transparent',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    📜 Historique ({history.length})
                                </button>
                            </div>

                            {/* Contenu selon l'onglet actif */}
                            {activeTab === 'search' ? (
                                <>
                                    {/* Section recherche */}
                                    <div style={{ padding: '24px', borderBottom: '1px solid rgba(99, 102, 241, 0.1)' }}>
                                        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#a78bfa', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span>🔍</span> Recherche
                                        </h2>

                                        <form onSubmit={handleSearch} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Activité NAF</label>
                                                <select value={selectedActivity} onChange={e => setSelectedActivity(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }}>
                                                    <option value="">{loadingActivities ? 'Chargement...' : 'Sélectionnez...'}</option>
                                                    {activities.map(act => <option key={act.value} value={act.value}>{act.value} - {act.label}</option>)}
                                                </select>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Département</label>
                                                    <input type="text" placeholder="Ex: 68" value={codePostal} onChange={e => setCodePostal(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Code Postal</label>
                                                    <input type="text" placeholder="Ex: 68000" value={codePostalComplet} onChange={e => setCodePostalComplet(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Limite</label>
                                                    <input type="number" value={nbResult} onChange={e => setNbResult(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                                </div>
                                            </div>

                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8 }}>Rayon: <span style={{ color: '#a78bfa', fontWeight: 700 }}>{rayon} km</span></label>
                                                <input type="range" min="1" max="100" value={rayon} onChange={e => setRayon(e.target.value)} style={{ width: '100%', accentColor: '#6366f1' }} />
                                            </div>

                                            {/* Bouton de géolocalisation */}
                                            <button
                                                type="button"
                                                onClick={getCurrentLocation}
                                                disabled={locating}
                                                style={{
                                                    width: '100%',
                                                    background: locating ? 'linear-gradient(135deg, #9ca3af, #6b7280)' : 'linear-gradient(135deg, #10b981, #059669)',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: 15,
                                                    border: 'none',
                                                    borderRadius: 10,
                                                    padding: '12px',
                                                    cursor: locating ? 'not-allowed' : 'pointer',
                                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                                    transition: 'all 0.2s',
                                                    marginBottom: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {locating ? (
                                                    <>
                                                        <div style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            border: '2px solid transparent',
                                                            borderTop: '2px solid white',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}></div>
                                                        Localisation en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        📍 Me localiser
                                                    </>
                                                )}
                                            </button>

                                            <button
                                                type="submit"
                                                disabled={searchLoading}
                                                style={{
                                                    width: '100%',
                                                    background: searchLoading ? 'rgba(99, 102, 241, 0.5)' : 'linear-gradient(135deg, #6366f1, #a855f7)',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: 15,
                                                    border: 'none',
                                                    borderRadius: 10,
                                                    padding: '12px',
                                                    cursor: searchLoading ? 'not-allowed' : 'pointer',
                                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {searchLoading ? (
                                                    <>
                                                        <div style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            border: '2px solid transparent',
                                                            borderTop: '2px solid white',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}></div>
                                                        Recherche en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        🚀 Lancer la recherche
                                                    </>
                                                )}
                                            </button>
                                        </form>
                                    </div>

                                    {/* Section résultats */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(99, 102, 241, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div>
                                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#a78bfa', marginBottom: 4 }}>📊 Résultats</h3>
                                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                                    {isResultsArray ? results.length : 0} entreprises • {isResultsArray ? results.filter(e => e.latitude && e.longitude).length : 0} géolocalisées
                                                </div>
                                            </div>
                                            <button onClick={() => setShowList(!showList)} style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#a78bfa', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                {showList ? '📋' : '👁️'}
                                            </button>
                                        </div>

                                        {showList && (
                                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                                                {isResultsArray && results.length > 0 ? (
                                                    results.map((etab, idx) => (
                                                        <div key={etab.id || idx} onClick={() => { setSelectedEtab(etab); if (etab.latitude && etab.longitude) setMapCenter([parseFloat(etab.latitude), parseFloat(etab.longitude)]); }} style={{
                                                            padding: '14px',
                                                            marginBottom: '8px',
                                                            background: selectedEtab && selectedEtab.id === etab.id ? 'linear-gradient(135deg, rgba(99, 102,241, 0.15), rgba(168, 85, 247, 0.1))' : 'rgba(15, 20, 32, 0.6)',
                                                            border: selectedEtab && selectedEtab.id === etab.id ? '1px solid rgba(99, 102,241, 0.4)' : '1px solid rgba(99, 102, 241, 0.1)',
                                                            borderRadius: 10,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}>
                                                            <div style={{ fontWeight: 700, fontSize: 14, color: '#e5e7eb', marginBottom: 6 }}>
                                                                {etab.denomination_usuelle || etab.nom_entreprise || etab.siren}
                                                            </div>
                                                            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
                                                                <span style={{ color: '#6366f1', fontWeight: 600 }}>SIRET:</span> {etab.siret}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#6b7280' }}>
                                                                {etab.adresse} • {etab.code_postal} {etab.commune}
                                                            </div>
                                                            <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                <span style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(99, 102, 241, 0.15)', color: '#a78bfa', borderRadius: 6, fontWeight: 600 }}>
                                                                    {etab.code_naf}
                                                                </span>
                                                                {etab.etat_administratif === 'A' && (
                                                                    <span style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', borderRadius: 6, fontWeight: 600 }}>
                                                                        ✓ Actif
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', fontSize: 14 }}>
                                                        Aucun résultat.<br />Lancez une recherche pour afficher les entreprises.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                /* Onglet Historique */
                                <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                                    <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#a78bfa', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span>📜</span> Historique ({history.length})
                                    </h2>

                                    {history && history.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {history.map((entry, idx) => (
                                                <div key={idx} style={{
                                                    padding: '16px',
                                                    background: 'rgba(15, 20, 32, 0.6)',
                                                    border: '1px solid rgba(99, 102, 241, 0.1)',
                                                    borderRadius: 10,
                                                    transition: 'all 0.2s'
                                                }}>
                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>
                                                        🕐 {entry.date}
                                                    </div>
                                                    <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12, lineHeight: 1.6 }}>
                                                        <div style={{ marginBottom: 6 }}><span style={{ color: '#6b7280' }}>📌 NAF:</span> <span style={{ color: '#a78bfa', fontWeight: 600 }}>{entry.code_naf || entry._original?.naf_code || '-'}</span></div>
                                                        <div style={{ marginBottom: 6 }}>
                                                            <span style={{ color: '#6b7280' }}>📍 Localisation:</span>
                                                            <span style={{ color: '#e5e7eb' }}>
                                                                {(() => {
                                                                    const codePostalValue = entry.code_postal || entry._original?.department || '';
                                                                    if (codePostalValue.length === 5) {
                                                                        return `${codePostalValue} (Dép. ${codePostalValue.substring(0, 2)})`;
                                                                    } else if (codePostalValue.length === 2) {
                                                                        return `Département ${codePostalValue}`;
                                                                    }
                                                                    return codePostalValue || '-';
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <div style={{ marginBottom: 6 }}><span style={{ color: '#6b7280' }}>📏 Rayon:</span> <span style={{ color: '#e5e7eb' }}>{entry.rayon || entry._original?.rayon || '-'} km</span></div>
                                                        <div><span style={{ color: '#6b7280' }}>📊 Résultats:</span> <span style={{ color: '#e5e7eb' }}>{entry.limit || entry._original?.nombre_resultats || 100}</span></div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button
                                                            onClick={() => {
                                                                // Utiliser les bonnes propriétés pour relancer
                                                                const searchData = {
                                                                    code_naf: entry.code_naf || entry._original?.naf_code || '',
                                                                    code_postal: entry.code_postal || entry._original?.department || '',
                                                                    rayon: entry.rayon || entry._original?.rayon || 10,
                                                                    limit: entry.limit || entry._original?.nombre_resultats || 100
                                                                };
                                                                handleRelancer(searchData);
                                                                setActiveTab('search');
                                                            }}
                                                            style={{
                                                                flex: 1,
                                                                background: 'rgba(99, 102, 241, 0.15)',
                                                                color: '#a78bfa',
                                                                border: '1px solid rgba(99, 102, 241, 0.3)',
                                                                borderRadius: 8,
                                                                padding: '8px',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            🔄 Relancer
                                                        </button>
                                                        <button
                                                            onClick={() => handleSupprimer(idx)}
                                                            style={{
                                                                flex: 1,
                                                                background: 'rgba(239, 68, 68, 0.15)',
                                                                color: '#f87171',
                                                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                                                borderRadius: 8,
                                                                padding: '8px',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            🗑️ Supprimer
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280', fontSize: 14 }}>
                                            <div style={{ fontSize: 48, marginBottom: 16 }}>📜</div>
                                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun historique</div>
                                            <div style={{ fontSize: 12 }}>Vos recherches apparaîtront ici</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </aside>

                        {/* Zone principale - Carte et détails */}
                        <section className="map-section">
                            {/* Carte */}
                            <div className="map-container">
                                <MapContainer
                                    center={mapCenter}
                                    zoom={6}
                                    scrollWheelZoom={true}
                                    style={{ height: '100%', width: '100%' }}
                                    whenReady={() => setMapReady(true)}
                                >
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                                    <FlyToMarker position={selectedEtab && selectedEtab.latitude && selectedEtab.longitude ? [parseFloat(selectedEtab.latitude), parseFloat(selectedEtab.longitude)] : null} />
                                    {/* Marqueur de position utilisateur */}
                                    {console.log('🗺️ Rendu carte - userLocation:', userLocation)}
                                    {userLocation && (
                                        <Marker
                                            position={userLocation}
                                            icon={userLocationIcon}
                                        >
                                            <Popup>
                                                <div style={{ minWidth: 150, textAlign: 'center' }}>
                                                    <div style={{ fontWeight: 700, marginBottom: 4, color: '#3B82F6' }}>📍 Votre position</div>
                                                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                                                        Lat: {userLocation[0].toFixed(6)}<br />
                                                        Lng: {userLocation[1].toFixed(6)}
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    )}
                                    {isResultsArray && results.map((etab, idx) => {
                                        let lat = parseFloat(etab.latitude);
                                        let lng = parseFloat(etab.longitude);
                                        if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
                                        const isSelected = selectedEtab && selectedEtab.id === etab.id;
                                        return (
                                            <Marker
                                                key={`marker-${etab.id || idx}`}
                                                position={[lat, lng]}
                                                icon={isSelected ? selectedIcon : entrepriseIcon}
                                                eventHandlers={{
                                                    click: () => {
                                                        setSelectedEtab(etab);
                                                        setMapCenter([lat, lng]);
                                                    }
                                                }}
                                            >
                                                <Popup>
                                                    <div style={{ minWidth: 200 }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 8 }}>{etab.denomination_usuelle || etab.siren}</div>
                                                        <div style={{ fontSize: 12, marginBottom: 4 }}>{etab.adresse}</div>
                                                        <div style={{ fontSize: 12, color: '#6366f1' }}>{etab.code_naf}</div>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        );
                                    })}
                                </MapContainer>

                                {/* Contrôles carte */}
                                <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 1000 }}>
                                    <button
                                        onClick={getCurrentLocation}
                                        disabled={locating}
                                        style={{
                                            background: locating ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.15)',
                                            backdropFilter: 'blur(12px)',
                                            color: locating ? '#9ca3af' : '#a78bfa',
                                            border: '1px solid rgba(139, 92, 246, 0.3)',
                                            borderRadius: 8,
                                            padding: '8px 14px',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            cursor: locating ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        title="Se localiser"
                                    >
                                        {locating ? (
                                            <>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    border: '2px solid transparent',
                                                    borderTop: '2px solid currentColor',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                Localisation...
                                            </>
                                        ) : (
                                            <>
                                                📍 Me localiser
                                            </>
                                        )}
                                    </button>
                                    <button onClick={() => setFullscreen(true)} style={{ background: 'rgba(15, 20, 32, 0.95)', backdropFilter: 'blur(12px)', color: '#a78bfa', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
                                        🔍 Plein écran
                                    </button>
                                    <button onClick={async () => {
                                        if (!results || results.length === 0) return alert('Aucun résultat');
                                        const res = await fetch(buildUrl('/api/export-mymaps'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ results, code_naf: selectedActivity, code_postal: codePostal, rayon }) });
                                        const blob = await res.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'export_mymaps.csv';
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        window.URL.revokeObjectURL(url);
                                    }} style={{ background: 'rgba(16, 185, 129, 0.15)', backdropFilter: 'blur(12px)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
                                        💾 Export CSV
                                    </button>
                                    <button onClick={async () => {
                                        if (!results || results.length === 0) {
                                            // Notification d'erreur stylée
                                            const errorNotification = document.createElement('div');
                                            errorNotification.style.cssText = `
                                            position: fixed;
                                            top: 80px;
                                            right: 24px;
                                            background: linear-gradient(135deg, rgba(251, 146, 60, 0.95), rgba(245, 158, 11, 0.95));
                                            backdrop-filter: blur(12px);
                                            color: #fff;
                                            padding: 16px 20px;
                                            border-radius: 12px;
                                            box-shadow: 0 8px 32px rgba(251, 146, 60, 0.4);
                                            border: 1px solid rgba(255, 255, 255, 0.2);
                                            z-index: 10000;
                                            font-family: 'Inter', sans-serif;
                                            font-size: 14px;
                                            font-weight: 600;
                                            display: flex;
                                            align-items: center;
                                            gap: 12px;
                                            animation: slideInRight 0.3s ease-out;
                                            min-width: 300px;
                                        `;
                                            errorNotification.innerHTML = `
                                            <span style="font-size: 20px;">🗺️</span>
                                            <div>
                                                <div style="font-weight: 700; margin-bottom: 4px;">Aucun résultat</div>
                                                <div style="font-size: 12px; opacity: 0.9;">Lancez une recherche pour exporter vers MyMaps</div>
                                            </div>
                                        `;
                                            document.body.appendChild(errorNotification);
                                            setTimeout(() => {
                                                errorNotification.style.animation = 'slideOutRight 0.3s ease-out';
                                                setTimeout(() => errorNotification.remove(), 300);
                                            }, 3000);
                                            return;
                                        }

                                        try {
                                            // Générer le fichier KML pour Google MyMaps
                                            const kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
        <name>Prospectys - Export ${new Date().toLocaleDateString('fr-FR')}</name>
        <description>Export des entreprises depuis Prospectys</description>
        <Style id="entreprise">
            <IconStyle>
                <Icon>
                    <href>http://maps.google.com/mapfiles/kml/paddle/red-circle.png</href>
                </Icon>
            </IconStyle>
        </Style>
        ${results.filter(etab => etab.latitude && etab.longitude).map(etab => `
        <Placemark>
            <name>${(etab.denomination_usuelle || etab.nom_entreprise || etab.siren || '').replace(/[<>&"']/g, '')}</name>
            <description><![CDATA[
                <b>SIRET:</b> ${etab.siret || ''}<br/>
                <b>Code NAF:</b> ${etab.code_naf || ''}<br/>
                <b>Adresse:</b> ${etab.adresse || ''}, ${etab.code_postal || ''} ${etab.commune || ''}<br/>
                <b>État:</b> ${etab.etat_administratif === 'A' ? 'Actif' : 'Inactif'}<br/>
                <b>Siège:</b> ${etab.est_siege ? 'Oui' : 'Non'}
            ]]></description>
            <styleUrl>#entreprise</styleUrl>
            <Point>
                <coordinates>${etab.longitude},${etab.latitude},0</coordinates>
            </Point>
        </Placemark>`).join('')}
    </Document>
</kml>`;

                                            // Télécharger le fichier KML
                                            const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `prospectys_export_${new Date().toISOString().split('T')[0]}.kml`;
                                            document.body.appendChild(a);
                                            a.click();
                                            a.remove();
                                            window.URL.revokeObjectURL(url);

                                            // Notification de succès
                                            const successNotification = document.createElement('div');
                                            successNotification.style.cssText = `
                                            position: fixed;
                                            top: 80px;
                                            right: 24px;
                                            background: linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.95));
                                            backdrop-filter: blur(12px);
                                            color: #fff;
                                            padding: 16px 20px;
                                            border-radius: 12px;
                                            box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4);
                                            border: 1px solid rgba(255, 255, 255, 0.2);
                                            z-index: 10000;
                                            font-family: 'Inter', sans-serif;
                                            font-size: 14px;
                                            font-weight: 600;
                                            display: flex;
                                            align-items: center;
                                            gap: 12px;
                                            animation: slideInRight 0.3s ease-out;
                                            min-width: 320px;
                                        `;
                                            successNotification.innerHTML = `
                                            <span style="font-size: 20px;">✅</span>
                                            <div>
                                                <div style="font-weight: 700; margin-bottom: 4px;">Export MyMaps réussi !</div>
                                                <div style="font-size: 12px; opacity: 0.9;">Importez le fichier KML dans Google MyMaps</div>
                                            </div>
                                        `;
                                            document.body.appendChild(successNotification);
                                            setTimeout(() => {
                                                successNotification.style.animation = 'slideOutRight 0.3s ease-out';
                                                setTimeout(() => successNotification.remove(), 300);
                                            }, 4000);

                                        } catch (error) {
                                            console.error('Erreur export MyMaps:', error);
                                            // Notification d'erreur
                                            const errorNotification = document.createElement('div');
                                            errorNotification.style.cssText = `
                                            position: fixed;
                                            top: 80px;
                                            right: 24px;
                                            background: linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95));
                                            backdrop-filter: blur(12px);
                                            color: #fff;
                                            padding: 16px 20px;
                                            border-radius: 12px;
                                            box-shadow: 0 8px 32px rgba(239, 68, 68, 0.4);
                                            border: 1px solid rgba(255, 255, 255, 0.2);
                                            z-index: 10000;
                                            font-family: 'Inter', sans-serif;
                                            font-size: 14px;
                                            font-weight: 600;
                                            display: flex;
                                            align-items: center;
                                            gap: 12px;
                                            animation: slideInRight 0.3s ease-out;
                                            min-width: 300px;
                                        `;
                                            errorNotification.innerHTML = `
                                            <span style="font-size: 20px;">⚠️</span>
                                            <div>
                                                <div style="font-weight: 700; margin-bottom: 4px;">Erreur d'export</div>
                                                <div style="font-size: 12px; opacity: 0.9;">Impossible de générer le fichier MyMaps</div>
                                            </div>
                                        `;
                                            document.body.appendChild(errorNotification);
                                            setTimeout(() => {
                                                errorNotification.style.animation = 'slideOutRight 0.3s ease-out';
                                                setTimeout(() => errorNotification.remove(), 300);
                                            }, 4000);
                                        }
                                    }} style={{ background: 'rgba(139, 92, 246, 0.15)', backdropFilter: 'blur(12px)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
                                        🗺️ Export MyMaps
                                    </button>
                                </div>
                            </div>

                            {/* Panneau détail entreprise (bas) */}
                            {selectedEtab && (
                                <div style={{
                                    background: 'linear-gradient(180deg, #0f1420 0%, #1a1f35 100%)',
                                    borderTop: '1px solid rgba(99, 102, 241, 0.2)',
                                    padding: '20px 24px',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#e5e7eb', marginBottom: 12 }}>
                                                {selectedEtab.denomination_usuelle || selectedEtab.nom_entreprise || selectedEtab.siren}
                                            </h3>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                                                <div><span style={{ color: '#9ca3af' }}>SIRET:</span> <span style={{ color: '#e5e7eb', fontWeight: 600 }}>{selectedEtab.siret}</span></div>
                                                <div><span style={{ color: '#9ca3af' }}>NAF:</span> <span style={{ color: '#a78bfa', fontWeight: 600 }}>{selectedEtab.code_naf}</span></div>
                                                <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#9ca3af' }}>Adresse:</span> <span style={{ color: '#e5e7eb' }}>{selectedEtab.adresse}, {selectedEtab.code_postal} {selectedEtab.commune}</span></div>
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedEtab(null)} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginLeft: 16 }}>
                                            ✕ Fermer
                                        </button>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                </main>

                {/* Modal Fullscreen */}
                {fullscreen && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#0a0e1a', zIndex: 9999 }}>
                        <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 1001 }}>
                            <button onClick={() => setFullscreen(false)} style={{ background: 'rgba(239, 68, 68, 0.2)', backdropFilter: 'blur(12px)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
                                ✕ Fermer
                            </button>
                        </div>
                        <MapContainer
                            center={mapCenter}
                            zoom={6}
                            scrollWheelZoom={true}
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <FlyToMarker position={selectedEtab && selectedEtab.latitude && selectedEtab.longitude ? [parseFloat(selectedEtab.latitude), parseFloat(selectedEtab.longitude)] : null} />
                            {/* Marqueur de position utilisateur en plein écran */}
                            {console.log('🗺️ Rendu plein écran - userLocation:', userLocation)}
                            {userLocation && (
                                <Marker
                                    position={userLocation}
                                    icon={userLocationIcon}
                                >
                                    <Popup>
                                        <div style={{ minWidth: 150, textAlign: 'center' }}>
                                            <div style={{ fontWeight: 700, marginBottom: 4, color: '#3B82F6' }}>📍 Votre position</div>
                                            <div style={{ fontSize: 12, color: '#6B7280' }}>
                                                Lat: {userLocation[0].toFixed(6)}<br />
                                                Lng: {userLocation[1].toFixed(6)}
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            )}
                            {isResultsArray && results.map((etab, idx) => {
                                let lat = parseFloat(etab.latitude);
                                let lng = parseFloat(etab.longitude);
                                if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
                                return (
                                    <Marker
                                        key={`fullscreen-marker-${etab.id || idx}`}
                                        position={[lat, lng]}
                                        icon={entrepriseIcon}
                                        eventHandlers={{
                                            click: () => {
                                                setSelectedEtab(etab);
                                                setMapCenter([lat, lng]);
                                            }
                                        }}
                                    >
                                        <Popup>
                                            <div style={{ minWidth: 200 }}>
                                                <div style={{ fontWeight: 700, marginBottom: 8 }}>{etab.denomination_usuelle || etab.siren}</div>
                                                <div style={{ fontSize: 12, marginBottom: 4 }}>{etab.adresse}</div>
                                                <div style={{ fontSize: 12, color: '#6366f1' }}>{etab.code_naf}</div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MapContainer>
                    </div>
                )}
            </div>
        </>
    );
}
